import { useMemo } from 'react'
import GeoJSON from 'geojson'

import { Coordinates } from '@/domain/protocols/Coordinates'

import { stackIds } from '../../containers/MapStackPresenter'
import { MapCircleLayer } from '../MapView/MapCircleLayer'
import { MapGeoJsonSource } from '../MapView/MapGeoJsonSource'
import { MapSymbolLayer } from '../MapView/MapSymbolLayer'
import { makeGeoJsonTocTodFeatureCollection } from './GeojsonUtils'
import {
  TocIcon,
  tocLabelLayoutStyle,
  tocPointLayoutStyle,
  tocPointPaintStyle,
  TodIcon,
  todLabelLayoutStyle,
  todPointLayoutStyle,
  todPointPaintStyle
} from './MapTocAndTod.styles'

export interface MapTocAndTodProps {
  distanceToTocOnStage: number
  tocRouteLegCoords: Coordinates[]

  distanceToTodOnStage: number
  todRouteLegCoords: Coordinates[]
}

/**
 * Top of climb and top of descend visualization for a Mapbox map
 *
 * @param distanceToToc the distance on route to the TOC
 * @param tocRouteLegCoords the coordinates of the route' leg where the top of climb is present
 * @param distanceToTod the distance on route to the TOD
 * @param todRouteLegCoords the coordinates of the route' leg where the top of descend is present
 */
function MapTocAndTod(props: MapTocAndTodProps) {
  const { distanceToTocOnStage, tocRouteLegCoords, distanceToTodOnStage, todRouteLegCoords } = props

  const geoJsonTocPointFeatureCollection: GeoJSON.FeatureCollection = useMemo(() => {
    if (!distanceToTocOnStage || !tocRouteLegCoords) return null
    return makeGeoJsonTocTodFeatureCollection(distanceToTocOnStage, tocRouteLegCoords)()
  }, [distanceToTocOnStage, tocRouteLegCoords])

  const geoJsonTodPointFeatureCollection: GeoJSON.FeatureCollection = useMemo(() => {
    if (!distanceToTodOnStage || !todRouteLegCoords) return null
    return makeGeoJsonTocTodFeatureCollection(distanceToTodOnStage, todRouteLegCoords)()
  }, [distanceToTodOnStage, todRouteLegCoords])

  const ids = {
    source: {
      toc: 'tocLabelSource',
      tod: 'todLabelSource'
    },
    pointLayer: {
      toc: 'tocPointLayer',
      tod: 'todPointLayer'
    },
    labelLayer: {
      toc: 'tocLabelLayer',
      tod: 'todLabelLayer'
    }
  }

  return (
    <>
      {/* TOC */}
      <MapGeoJsonSource id={ids.source.toc} data={geoJsonTocPointFeatureCollection}>
        {/* TOC POINT */}
        <MapCircleLayer
          id={ids.pointLayer.toc}
          sourceId={ids.source.toc}
          layout={tocPointLayoutStyle()}
          paint={tocPointPaintStyle()}
          beforeLayerId={stackIds.route.TocAndTod}
        />
        {/* TOC LABEL */}
        <MapSymbolLayer
          id={ids.labelLayer.toc}
          sourceId={ids.source.toc}
          imageSrc={TocIcon}
          layout={tocLabelLayoutStyle()}
          beforeLayerId={stackIds.route.TocAndTod}
        />
      </MapGeoJsonSource>

      {/* TOD */}
      <MapGeoJsonSource id={ids.source.tod} data={geoJsonTodPointFeatureCollection}>
        {/* TOD POINT */}
        <MapCircleLayer
          id={ids.pointLayer.tod}
          sourceId={ids.source.tod}
          layout={todPointLayoutStyle()}
          paint={todPointPaintStyle()}
          beforeLayerId={stackIds.route.TocAndTod}
        />
        {/* TOD LABEL */}
        <MapSymbolLayer
          id={ids.labelLayer.tod}
          sourceId={ids.source.tod}
          imageSrc={TodIcon}
          layout={todLabelLayoutStyle()}
          beforeLayerId={stackIds.route.TocAndTod}
        />
      </MapGeoJsonSource>
    </>
  )
}

export { MapTocAndTod }
